import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { CameraCapabilities, CameraControlSettings, CameraService } from '../../services/camera.service';

@Component({
  selector: 'app-camera-card',
  standalone: true,
  imports: [NgIf, FormsModule],
  templateUrl: './camera-card.component.html',
  styleUrl: './camera-card.component.css'
})
export class CameraCardComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) deviceId!: string;
  @Input({ required: true }) label!: string;
  @ViewChild('preview') previewElement?: ElementRef<HTMLVideoElement>;

  isLoading = true;
  errorMessage = '';
  zoom = 0;
  focusDistance = 0;
  focusMode = '';
  capabilities: CameraCapabilities | null = null;

  private stream: MediaStream | null = null;
  private destroyed = false;
  private zoomTimer: ReturnType<typeof setTimeout> | null = null;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private cameraService: CameraService) {}

  async ngAfterViewInit() {
    await this.startPreview();
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.zoomTimer) {
      clearTimeout(this.zoomTimer);
    }
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
    this.stopPreview();
  }

  get hasAnyControls(): boolean {
    return !!this.capabilities && (this.capabilities.supportsZoom || this.capabilities.supportsManualFocus || this.capabilities.supportsAutoFocus);
  }

  get showFocusDistance(): boolean {
    return !!this.capabilities?.supportsManualFocus && this.focusMode === 'manual';
  }

  async onZoomChange() {
    if (this.zoomTimer) {
      clearTimeout(this.zoomTimer);
    }
    this.zoomTimer = setTimeout(() => {
      void this.applyAndSave({ zoom: this.zoom });
    }, 80);
  }

  async onFocusModeChange() {
    const controls: CameraControlSettings = { focusMode: this.focusMode };
    if (this.focusMode === 'manual') {
      controls.focusDistance = this.focusDistance;
    }
    await this.applyAndSave(controls);
  }

  async onFocusDistanceChange() {
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
    this.focusTimer = setTimeout(() => {
      void this.applyAndSave({
        focusMode: 'manual',
        focusDistance: this.focusDistance
      });
    }, 80);
  }

  formatValue(value: number): string {
    if (!Number.isFinite(value)) {
      return '';
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  private async startPreview() {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: this.deviceId } }
      });

      if (this.destroyed) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      this.stream = stream;

      const track = stream.getVideoTracks()[0];
      this.capabilities = this.cameraService.getCapabilities(track);

      await this.cameraService.applySavedControls(track);

      const current = this.cameraService.getCurrentControls(track);
      const saved = this.cameraService.getSavedControls(this.deviceId);
      this.zoom = saved.zoom ?? current.zoom ?? this.capabilities.zoom?.min ?? 0;
      this.focusDistance = saved.focusDistance ?? current.focusDistance ?? this.capabilities.focusDistance?.min ?? 0;
      this.focusMode = saved.focusMode ?? current.focusMode ?? this.capabilities.autoFocusMode ?? (this.capabilities.supportsManualFocus ? 'manual' : '');

      const video = this.previewElement?.nativeElement;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
    } catch (error) {
      console.error(`Unable to open camera ${this.label}:`, error);
      this.errorMessage = 'Unable to open this camera. It may already be in use.';
      this.stopPreview();
    } finally {
      this.isLoading = false;
    }
  }

  private async applyAndSave(controls: CameraControlSettings) {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) {
      return;
    }

    await this.cameraService.applyControls(track, controls);
    this.cameraService.saveControls(this.deviceId, controls);
  }

  private stopPreview() {
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
    if (this.previewElement?.nativeElement) {
      this.previewElement.nativeElement.srcObject = null;
    }
  }
}
